import { css } from '@emotion/react'
import React from 'react'
import Link from 'next/link'
import { Card, colors, font, mq } from 'plume-ui'
import { stripMarkdown } from '@/lib/markdown'
import { articlePath, ARTICLE_HREF } from '@/lib/url'
import { Article } from '../../types'
import { LineClampText } from '../atoms/LineClampText'
import { TagButton, TagButtonGroup } from '../atoms/TagButton'
import { LockIcon } from '../pages/articles/LockIcon'
import { FormattedDate } from '../atoms/FormattedDate'

type Props = {
  article: Article
}

export const FullwidthArticleCard: React.FC<Props> = (props) => {
  return (
    <Card
      css={css`
        border-bottom: 1px solid ${colors.gray50};
      `}
    >
      <Card.Content>
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr 120px;
            ${mq.large} {
              grid-template-columns: 160px 1fr;
            }
          `}
        >
          <div
            css={css`
              grid-row: 1 / 2;
              grid-column: 2 / 3;
              margin-left: 8px;
              ${mq.large} {
                grid-row: 1 / 3;
                grid-column: 1 / 2;
                margin: 0 16px 0 0;
              }
            `}
          >
            <Link href={ARTICLE_HREF} as={articlePath(props.article)}>
              <a
                css={css`
                  display: block;
                  position: relative;
                  border-radius: 2px;
                  &::before {
                    content: '';
                    display: block;
                    width: 100%;
                    height: 100%;
                    background-color: transparent;
                    border-radius: 2px;
                    position: absolute;
                    transition: background-color 0.2s ease;
                  }
                  &:hover::before {
                    background-color: rgba(0, 0, 0, 0.08);
                  }
                `}
              >
                <img
                  css={css`
                    display: block;
                    border-radius: 2px;
                    width: 100%;
                    object-fit: contain;
                    aspect-ratio: 1.91 / 1;
                  `}
                  src={props.article.headerImage.url}
                />
              </a>
            </Link>
          </div>
          <div
            css={css`
              grid-row: 1 / 2;
              grid-column: 1 / 2;
              ${mq.large} {
                grid-row: 1 / 2;
                grid-column: 2 / 3;
              }
            `}
          >
            <LockIcon article={props.article} />
            <Link href={ARTICLE_HREF} as={articlePath(props.article)}>
              <a>
                <span
                  css={css`
                    color: ${colors.black10};
                    font-size: 15px;
                    line-height: 1.5;
                    font-weight: ${font.weight.bold};
                    letter-spacing: 0.02em;
                    text-decoration: underline solid transparent;
                    transition: text-decoration 0.2s ease 0s;
                    &:hover {
                      text-decoration: underline solid Currentcolor;
                    }
                    ${mq.large} {
                      font-size: 14px;
                    }
                  `}
                >
                  {props.article.title}
                </span>
              </a>
            </Link>
            <p
              css={css`
                margin: 4px 0 0;
                color: ${colors.textLight};
                font-size: 12px;
                letter-spacing: 0.02em;
                line-height: 1.25;
              `}
            >
              <FormattedDate>{props.article.publishedAt}</FormattedDate>
            </p>
            <p
              css={css`
                display: none;
                margin: 4px 0 0;
                ${mq.large} {
                  display: block;
                }
              `}
            >
              <LineClampText lineNumber={2} size={'12px'} height={1.5}>
                {stripMarkdown(props.article.introduction)}
              </LineClampText>
            </p>
          </div>
          <div
            css={css`
              margin-top: 4px;
              grid-row: 2 / 3;
              grid-column: 1 / 3;
              ${mq.large} {
                grid-row: 2 / 3;
                grid-column: 2 / 3;
              }
            `}
          >
            <TagButtonGroup>
              {props.article.newTags?.map((tag, i) => (
                <TagButton tag={tag} key={i} />
              ))}
            </TagButtonGroup>
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}
